import { extend } from "lodash";

import type {
  PublicTripLeg,
  RelatedPublicSegment,
  RelatedPublicSegmentV4,
  RelatedPublicTrip,
  RelatedPublicTripV4
} from "@busbud/core-types/dist/lib/poll-service/api";

import type { TicketOptionsCardName } from "@app/components/drawers/selection-options/selection-options.constants";
import { CHECKOUT_STEPS } from "@app/modules/checkout/constants";
import { filterTripCartItems } from "@app/modules/checkout/utils/cart-v2";
import type { ResultsSortKey } from "@app/modules/results/utils/sort-definitions";
import type { TTLWarningModal } from "@app/types/modal";
import type { AlternativeRoute } from "@app/types/napi-types";
import type {
  NapiV2Cart,
  NapiV2CartSegment
} from "@app/types/napi-v2-cart-types";
import type { TrackingAnalytics } from "@app/types/tracking";
import type { AmplitudeEvent } from "@app/types/tracking/common";
import type {
  CardToggled,
  CartCreationError,
  CartCreationPriceChanged,
  CartCreationPriceChangedAccepted,
  CartCreationPriceChangedRefused,
  CartCreationPriceChangedTemplate,
  CartUpdate,
  ClickedAlternativeRoute,
  ClickedCancelWheelchairSearch,
  ClosedBusDetails,
  DisruptionClosed,
  DisruptionDisplayed,
  DisruptionLinkClicked,
  DownloadMobileAppModalAppIconClicked,
  DownloadMobileAppModalDisplayed,
  ErrorAdBanner,
  LoadedBusDetails,
  LoadedInterconnections,
  LoadedResults,
  OpenedBusDetails,
  PickedGenderBusDetails,
  SelectDateTab,
  SelectedDepartureDetails,
  SelectedDepartureDetailsAsyncCart,
  ShowedTtlError,
  SortedResults,
  SuspendedOperationsAlertClosed,
  SuspendedOperationsAlertDisplayed,
  SuspendedOperationsAlertLinkClicked,
  ToggleFilters,
  TripReconciliationFailed,
  PriceLockSmokeTest,
  CartCreationSucceedDetails
} from "@app/types/tracking/results";

import * as PropertyValues from "./constants/property-values";
import { ResultsTrackingAction } from "./constants/tracking-action";
import {
  buildAddToCartEvent,
  buildCheckoutProperties,
  buildResultsProperties,
  buildSearchProperties
} from "./ga4/event";
import { getTripInformation } from "./helpers/udm";
import { Money } from "../lib/money";
import { hasAnySeatSelection } from "./helpers/udm/vehicle";

export function loadedTripResults(
  analytics: TrackingAnalytics,
  details: LoadedResults,
  is_valid_route: boolean
): AmplitudeEvent<LoadedResults> {
  // TODO: Check with marketing squad before removing it because it is a GTM attribute but this is duplicate of result_outbound|return_content that is included in the section_parameters.
  let label = "nosupply";
  if (is_valid_route) {
    label = "empty";
    if (details.results_displayed_count > 0) {
      label = "nodeal";
      if (details.results_bob_count > 0) {
        label = "BoB";
      } else if (details.results_box_count > 0) {
        label = "BoX";
      }
    }
  }

  return {
    action: ResultsTrackingAction.LoadedResults,
    trigger: "Non-interaction event",
    category: "results",
    label,
    details,
    gtm_props: {
      ...buildSearchProperties(analytics.section_properties),
      ...buildResultsProperties(details)
    }
  };
}

export function openedBusDetails(
  analytics: TrackingAnalytics,
  departure_status: string,
  has_layout: boolean
): AmplitudeEvent<OpenedBusDetails> {
  return {
    category: "results",
    label: analytics.section_properties.search_route,
    action: ResultsTrackingAction.OpenedBusDetails,
    details: {
      opened_bus_departure_status: departure_status,
      opened_bus_has_layout: has_layout
    }
  };
}

export function closedBusDetails(
  analytics: TrackingAnalytics,
  departure_status: string,
  has_layout: boolean
): AmplitudeEvent<ClosedBusDetails> {
  return {
    category: "results",
    label: analytics.section_properties.search_route,
    action: ResultsTrackingAction.ClosedBusDetails,
    details: {
      opened_bus_departure_status: departure_status,
      opened_bus_has_layout: has_layout
    }
  };
}

export function loadedBusDetails(
  analytics: TrackingAnalytics,
  departure_status: string,
  has_layout: boolean
): AmplitudeEvent<LoadedBusDetails> {
  return {
    category: "results",
    label: analytics.section_properties.search_route,
    action: ResultsTrackingAction.LoadedBusDetails,
    details: {
      opened_bus_has_layout: has_layout,
      opened_bus_departure_status: departure_status
    }
  };
}

export function loadedBusDetailsFailed(
  analytics: TrackingAnalytics,
  departure_status: string,
  has_layout: boolean
): AmplitudeEvent<LoadedBusDetails> {
  return {
    category: "results",
    label: analytics.section_properties.search_route,
    action: ResultsTrackingAction.LoadedBusDetailsFailed,
    details: {
      opened_bus_has_layout: has_layout,
      opened_bus_departure_status: departure_status
    }
  };
}

export function pickedGenderBusDetails(
  analytics: TrackingAnalytics,
  seat_number: string,
  gender: string
): AmplitudeEvent<PickedGenderBusDetails> {
  return {
    category: "results",
    label: analytics.section_properties.search_route,
    action: ResultsTrackingAction.PickedGenderBusDetails,
    details: { seat_number, gender }
  };
}

export function ticketOptionsPageview(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>
): AmplitudeEvent<SelectedDepartureDetails> {
  const details = getSelectedDepartureDetails(analytics, false);

  return {
    action: ResultsTrackingAction.ViewTicketOptions,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details,
    ecommerce: selectedTripEcommerceData(details, analytics.section_properties)
  };
}

export function seatSelectionPageview(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>
): AmplitudeEvent<SelectedDepartureDetails> {
  const details = getSelectedDepartureDetails(analytics, true);

  return {
    action: ResultsTrackingAction.ViewSeatSelection,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details,
    ecommerce: selectedTripEcommerceData(details, analytics.section_properties)
  };
}

export function asyncCartPageview(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>
): AmplitudeEvent<SelectedDepartureDetailsAsyncCart> {
  const details = getSelectedDepartureDetails(analytics, true);

  return {
    action: ResultsTrackingAction.ViewAsyncCart,
    category: "checkout",
    label: getTripInformation(trip, trip_leg, segments),
    details: {
      ...details,
      // properties to match checkout_pageview event
      checkout_step: CHECKOUT_STEPS.PASSENGERS,
      selection_outbound_source: analytics.section_properties.selection_source
    },
    ecommerce: selectedTripEcommerceData(details, analytics.section_properties)
  };
}

function selectedTripEcommerceData(
  details: SelectedDepartureDetails,
  section_properties: Record<string, string>
): unknown {
  const direction = section_properties.selection_return_type
    ? "return"
    : "outbound";

  const type = section_properties[`selection_${direction}_type`];

  if (type !== PropertyValues.BOB) {
    return;
  }

  return {
    add: {
      products: [
        {
          id: details.dep_route,
          name: details.dep_route,
          price: section_properties[`selection_${direction}_price_avg`],
          brand: section_properties[`selection_${direction}_source`],
          variant: section_properties[`selection_${direction}_operator`],
          category: type,
          dimension10: details.dep_origin_country
        }
      ]
    }
  };
}

export function selectedTrip(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean = false
): AmplitudeEvent<SelectedDepartureDetails> {
  const details = getSelectedDepartureDetails(analytics, is_async_cart);

  return {
    action: ResultsTrackingAction.SelectedDeparture,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details,
    gtm_props: buildAddToCartEvent(analytics.section_properties),
    ecommerce: selectedTripEcommerceData(details, analytics.section_properties)
  };
}

function ticketOptionsCardToggled(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean = false,
  card_name: TicketOptionsCardName,
  action: ResultsTrackingAction
): AmplitudeEvent<SelectedDepartureDetails & CardToggled> {
  const details = {
    ...getSelectedDepartureDetails(analytics, is_async_cart),
    card_name
  };

  return {
    action,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details,
    ecommerce: selectedTripEcommerceData(details, analytics.section_properties)
  };
}

export function ticketOptionsCardCollapsed(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean = false,
  card_name: TicketOptionsCardName
) {
  return ticketOptionsCardToggled(
    analytics,
    trip,
    trip_leg,
    segments,
    is_async_cart,
    card_name,
    ResultsTrackingAction.CardCollapsed
  );
}

export function ticketOptionsCardExpanded(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean = false,
  card_name: TicketOptionsCardName
) {
  return ticketOptionsCardToggled(
    analytics,
    trip,
    trip_leg,
    segments,
    is_async_cart,
    card_name,
    ResultsTrackingAction.CardExpanded
  );
}

export function cartCreationStarted(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean = false
): AmplitudeEvent<SelectedDepartureDetails> {
  const details = getSelectedDepartureDetails(analytics, is_async_cart);

  return {
    action: ResultsTrackingAction.CartCreationStarted,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details,
    ecommerce: selectedTripEcommerceData(details, analytics.section_properties)
  };
}

export function cartCreationSucceed(
  analytics: TrackingAnalytics,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  cart: NapiV2Cart,
  is_async_cart: boolean = false
): AmplitudeEvent<CartCreationSucceedDetails> {
  const details = getCartCreationSucceedDetails(analytics, is_async_cart, cart);

  return {
    action: ResultsTrackingAction.CartCreationSucceed,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details,
    ecommerce: selectedTripEcommerceData(
      details as SelectedDepartureDetails,
      analytics.section_properties
    ),
    gtm_props: buildCheckoutProperties(cart)
  };
}

function cartCreationPriceChangedTemplate(
  action: ResultsTrackingAction,
  analytics: TrackingAnalytics,
  cart: NapiV2Cart,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean
): AmplitudeEvent<CartCreationPriceChangedTemplate> {
  const formatPrice = (price: number): number => new Money(price).amount;
  const selected_departure_details = getSelectedDepartureDetails(
    analytics,
    is_async_cart
  );
  const fees =
    typeof cart.charges.fees === "number"
      ? cart.charges.fees
      : cart.charges.fees.total;

  const details = extend(selected_departure_details, {
    cart_fees: formatPrice(fees),
    cart_taxes: formatPrice(cart.charges.taxes),
    cart_subtotal: formatPrice(cart.charges.subtotal),
    cart_total: formatPrice(cart.charges.total),
    cart_total_minus_taxes: formatPrice(cart.charges.total - cart.charges.taxes)
  });

  const label = getTripInformation(trip, trip_leg, segments);

  return {
    action,
    category: "results",
    label,
    details,
    trigger: "Non-interaction event"
  };
}

export function cartCreationPriceChanged(
  analytics: TrackingAnalytics,
  cart: NapiV2Cart,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean
): AmplitudeEvent<CartCreationPriceChanged> {
  return cartCreationPriceChangedTemplate(
    ResultsTrackingAction.CartCreationPriceChanged,
    analytics,
    cart,
    trip,
    trip_leg,
    segments,
    is_async_cart
  );
}

export function cartCreationPriceChangedAccepted(
  analytics: TrackingAnalytics,
  cart: NapiV2Cart,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean
): AmplitudeEvent<CartCreationPriceChangedAccepted> {
  return cartCreationPriceChangedTemplate(
    ResultsTrackingAction.CartCreationPriceChangedAccepted,
    analytics,
    cart,
    trip,
    trip_leg,
    segments,
    is_async_cart
  );
}

export function cartCreationPriceChangedRefused(
  analytics: TrackingAnalytics,
  cart: NapiV2Cart,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean
): AmplitudeEvent<CartCreationPriceChangedRefused> {
  return cartCreationPriceChangedTemplate(
    ResultsTrackingAction.CartCreationPriceChangedRefused,
    analytics,
    cart,
    trip,
    trip_leg,
    segments,
    is_async_cart
  );
}

export function cartCreationError(
  analytics: TrackingAnalytics,
  error_name: string,
  root_error_name: string | null,
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  is_async_cart: boolean
): AmplitudeEvent<CartCreationError> {
  const selected_departure_details = getSelectedDepartureDetails(
    analytics,
    is_async_cart
  );
  const details = extend(selected_departure_details, {
    error_name,
    root_error_name: root_error_name || PropertyValues.NA_STRING
  });

  return {
    action: ResultsTrackingAction.CartCreationError,
    category: "results",
    label: getTripInformation(trip, trip_leg, segments),
    details: details,
    trigger: "Non-interaction event"
  };
}

export function cartUpdateStarted(
  label: string,
  details: CartUpdate
): AmplitudeEvent<CartUpdate> {
  return {
    action: ResultsTrackingAction.CartUpdateStarted,
    category: "checkout",
    label,
    details
  };
}

export function cartUpdateSucceed(
  label: string,
  details: CartUpdate
): AmplitudeEvent<CartUpdate> {
  return {
    action: ResultsTrackingAction.CartUpdateSucceed,
    category: "checkout",
    label,
    details
  };
}

export function cartUpdateError(
  label: string,
  details: CartUpdate
): AmplitudeEvent<CartUpdate> {
  return {
    action: ResultsTrackingAction.CartUpdateError,
    category: "checkout",
    label,
    details
  };
}

export function clickedAlternativeRoute(
  route: AlternativeRoute,
  routes_count: number
): AmplitudeEvent<ClickedAlternativeRoute> {
  let cities_changed;
  const { origin_distance, destination_distance } = route;

  if (origin_distance > 0 && destination_distance > 0) {
    cities_changed = PropertyValues.CityChanged.Both;
  } else if (origin_distance > 0) {
    cities_changed = PropertyValues.CityChanged.Origin;
  } else {
    cities_changed = PropertyValues.CityChanged.Destination;
  }

  return {
    action: ResultsTrackingAction.ClickedAlternativeRoute,
    category: "results",
    details: {
      results_cities_changed: cities_changed,
      results_alternative_count: routes_count, // Number of alternative routes displayed to the user (4 or 5 if featured route enabled)
      results_rank: route.rank,
      results_alternative_route: `${route.origin.geohash}/${route.destination.geohash}`,
      results_alternative_route_names: `${route.origin.name}/${route.destination.name}`,
      results_delta_total: route.total_distance,
      results_delta_origin: route.origin_distance,
      results_delta_destination: route.destination_distance,
      results_alternative_route_departures_count: route.results_count
    }
  };
}

export function clickedNoResultsNextDate(): AmplitudeEvent<{}> {
  return {
    action: ResultsTrackingAction.ClickedNextAvailableDate,
    category: "results",
    details: {}
  };
}

export function clickedCancelWheelchairSearch(): AmplitudeEvent<ClickedCancelWheelchairSearch> {
  return {
    action: ResultsTrackingAction.ClickedCancelWheelchairSearch,
    category: "results",
    details: {}
  };
}

export function showedTtlError(
  tracking: TTLWarningModal["tracking"]
): AmplitudeEvent<ShowedTtlError> {
  let details = {};
  if (tracking) {
    // Take ownership of object for lifecycle of tracking event.
    details = Object.assign({}, tracking);
  }

  return {
    action: ResultsTrackingAction.ShowedTtlError,
    trigger: "Non-interaction event",
    category: "results",
    details
  };
}

export function sortedResults(
  type: ResultsSortKey
): AmplitudeEvent<SortedResults> {
  return {
    action: ResultsTrackingAction.SortedResults,
    category: "results",
    details: {
      results_sort_type: type
    }
  };
}

export function getCartCreationSucceedDetails(
  analytics: TrackingAnalytics,
  is_async_cart: boolean,
  cart: NapiV2Cart
): CartCreationSucceedDetails {
  const cart_trip_item = filterTripCartItems(cart.items)[0];
  // Roundtrip fake cart doesn't have segments
  const has_seat_selection = cart_trip_item?.segments
    ? hasAnySeatSelection(cart_trip_item?.segments as NapiV2CartSegment[])
    : false;

  const details: CartCreationSucceedDetails = {
    ...getSelectedDepartureDetails(analytics, is_async_cart),
    has_seat_selection
  };

  return details;
}

function getSelectedDepartureDetails(
  analytics: TrackingAnalytics,
  is_async_cart: boolean
): SelectedDepartureDetails {
  return {
    dep_origin: analytics.page.details.origin,
    dep_destination: analytics.page.details.destination,
    dep_origin_country: analytics.page.details.origin_country,
    dep_destination_country: analytics.page.details.destination_country,
    dep_route: analytics.page.details.route,
    is_async_cart
  };
}

export function disruptionDisplayed(
  operators: string[]
): AmplitudeEvent<DisruptionDisplayed> {
  return {
    action: ResultsTrackingAction.DisruptionDisplayed,
    category: "results",
    details: {
      impacted_operators: operators,
      displayed: true
    }
  };
}

export function disruptionClosed(
  operators: string[]
): AmplitudeEvent<DisruptionClosed> {
  return {
    action: ResultsTrackingAction.DisruptionClosed,
    category: "results",
    details: {
      impacted_operators: operators,
      closed: true
    }
  };
}

export function disruptionLinkClicked(
  link: string | null,
  operator_name: string | null
): AmplitudeEvent<DisruptionLinkClicked> {
  return {
    action: ResultsTrackingAction.DisruptionLinkClicked,
    category: "results",
    details: {
      link,
      link_name: operator_name
    }
  };
}

export function suspendedOperationsAlertDisplayed(): AmplitudeEvent<SuspendedOperationsAlertDisplayed> {
  return {
    action: ResultsTrackingAction.SuspendedOperationsAlertDisplayed,
    category: "results",
    details: {}
  };
}

export function suspendedOperationsAlertClosed(): AmplitudeEvent<SuspendedOperationsAlertClosed> {
  return {
    action: ResultsTrackingAction.SuspendedOperationsAlertClosed,
    category: "results",
    details: {}
  };
}

export function suspendedOperationsAlertLinkClicked(): AmplitudeEvent<SuspendedOperationsAlertLinkClicked> {
  return {
    action: ResultsTrackingAction.SuspendedOperationsAlertLinkClicked,
    category: "results",
    details: {}
  };
}

export function errorAdBanner(
  partner: string,
  error: string
): AmplitudeEvent<ErrorAdBanner> {
  return {
    action: ResultsTrackingAction.ErrorAdBanner,
    details: {
      partner,
      error
    }
  };
}

export function selectDateTab(
  date_diff: number,
  price: number | null,
  is_min_price: boolean
): AmplitudeEvent<SelectDateTab> {
  const price_type = price ? (is_min_price ? "cheapest" : "other") : "empty";

  return {
    action: ResultsTrackingAction.SelectDateTab,
    details: {
      results_selected_date_tab_difference: date_diff,
      results_selected_date_tab_displayed_price_type: price_type
    }
  };
}

// Filters
export function toggleFilters(
  filter_type: string,
  filter_selected_value: string | unknown[],
  filter_selected?: ToggleFilters["filter_selected"]
): AmplitudeEvent<ToggleFilters> {
  return {
    action: ResultsTrackingAction.ToggleFilters,
    category: "results",
    details: {
      filter_type: filter_type,
      filter_value: filter_selected_value,
      filter_selected
    }
  };
}

// Seat Selection First reconciliation
export function tripReconciliationFailed(
  error: string
): AmplitudeEvent<TripReconciliationFailed> {
  return {
    action: ResultsTrackingAction.TripReconciliationFailed,
    category: "results",
    trigger: "Non-interaction event",
    details: {
      reconciliation_error: error
    }
  };
}

export function loadedInterconnections(): AmplitudeEvent<LoadedInterconnections> {
  return {
    action: ResultsTrackingAction.LoadedInterconnections,
    category: "results",
    details: {}
  };
}

export function downloadMobileAppModalDisplayed(
  modal_type: DownloadMobileAppModalDisplayed["modal_type"]
): AmplitudeEvent<DownloadMobileAppModalDisplayed> {
  return {
    action: ResultsTrackingAction.DownloadMobileAppModalDisplayed,
    category: "results",
    details: {
      modal_type
    }
  };
}

export function downloadMobileAppModalAppIconClicked(
  modal_type: DownloadMobileAppModalAppIconClicked["modal_type"],
  platform: DownloadMobileAppModalAppIconClicked["platform"]
): AmplitudeEvent<DownloadMobileAppModalAppIconClicked> {
  return {
    action: ResultsTrackingAction.DownloadMobileAppModalAppIconClicked,
    category: "results",
    details: {
      modal_type,
      platform
    }
  };
}

export function ClickedPriceLockSmokeTest(): AmplitudeEvent<PriceLockSmokeTest> {
  return {
    action: ResultsTrackingAction.ClickedPriceLockSmokeTestV2,
    category: "results",
    details: {}
  };
}
